/**
 * Polish Public Holiday Calendar
 *
 * @description Calculates Polish public holidays for energy tariff calculations
 * @version 1.0.0
 * @created 2025-01-30
 *
 * Extracted from amplify/data/utils/polish-holidays.ts to eliminate cross-layer imports
 * Purpose: G13 tariff treats holidays as weekends (all-day off-peak rates)
 * Coverage: Years 2025-2030 (extensible to 1583-4099)
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Holiday Type Definition
 */
export interface Holiday {
  date: Date;
  namePL: string;
  nameEN: string;
  isFixed: boolean;
  type: 'religious' | 'secular' | 'national';
}

/**
 * Day Type Classification
 */
export type DayType = 'weekday' | 'weekend' | 'holiday';

/**
 * G13 Rate Zone Classification
 */
export type G13RateZone = 'peak' | 'offpeak' | 'night';

// ============================================================================
// EASTER CALCULATION
// ============================================================================

/**
 * Calculate Easter Sunday for a given year using the Meeus/Jones/Butcher algorithm
 *
 * This algorithm is accurate for Gregorian calendar years 1583-4099.
 * It uses modular arithmetic to determine the ecclesiastical full moon
 * and the subsequent Sunday.
 *
 * Algorithm source: Meeus, J. (1991). "Astronomical Algorithms"
 *
 * @param year - Gregorian calendar year (1583-4099)
 * @returns Easter Sunday date for the given year
 * @throws Error if year is outside valid range
 */
export function calculateEasterSunday(year: number): Date {
  // Input validation
  if (!Number.isInteger(year)) {
    throw new Error('Year must be an integer');
  }
  if (year < 1583 || year > 4099) {
    throw new Error('Year must be between 1583 and 4099 (Gregorian calendar range)');
  }

  // Meeus/Jones/Butcher algorithm
  const c = Math.floor(year / 100);
  const n = year - 19 * Math.floor(year / 19);
  const k = Math.floor((c - 17) / 25);

  let i = c - Math.floor(c / 4) - Math.floor((c - k) / 3) + 19 * n + 15;
  i = i % 30;
  i = i - Math.floor(i / 28) * (1 - Math.floor(i / 28) * Math.floor(29 / (i + 1)) * Math.floor((21 - n) / 11));

  const j = (year + Math.floor(year / 4) + i + 2 - c + Math.floor(c / 4)) % 7;
  const l = i - j;

  const month = 3 + Math.floor((l + 40) / 44);
  const day = l + 28 - 31 * Math.floor(month / 4);

  // Return Date object (month is 0-indexed in JavaScript)
  return new Date(year, month - 1, day);
}

// ============================================================================
// HOLIDAY CALCULATIONS
// ============================================================================

/**
 * Get all 12 Polish public holidays for a given year
 *
 * Returns array of holiday objects with dates, names (Polish and English),
 * and metadata. Includes both fixed holidays (same date every year) and
 * variable holidays (calculated based on Easter Sunday).
 *
 * @param year - Gregorian calendar year
 * @returns Array of 12 holiday objects, sorted by date
 */
export function getPolishHolidays(year: number): Holiday[] {
  // Validate input
  if (!Number.isInteger(year)) {
    throw new Error('Year must be an integer');
  }

  // Calculate Easter Sunday for variable holidays
  const easter = calculateEasterSunday(year);

  // Fixed holidays (9 total)
  const fixedHolidays: Holiday[] = [
    {
      date: new Date(year, 0, 1),  // January 1
      namePL: 'Nowy Rok',
      nameEN: "New Year's Day",
      isFixed: true,
      type: 'secular'
    },
    {
      date: new Date(year, 0, 6),  // January 6
      namePL: 'Święto Trzech Króli',
      nameEN: 'Epiphany',
      isFixed: true,
      type: 'religious'
    },
    {
      date: new Date(year, 4, 1),  // May 1
      namePL: 'Święto Pracy',
      nameEN: 'Labour Day',
      isFixed: true,
      type: 'secular'
    },
    {
      date: new Date(year, 4, 3),  // May 3
      namePL: 'Święto Konstytucji 3 Maja',
      nameEN: 'Constitution Day',
      isFixed: true,
      type: 'national'
    },
    {
      date: new Date(year, 7, 15),  // August 15
      namePL: 'Wniebowzięcie Najświętszej Maryi Panny',
      nameEN: 'Assumption of Mary',
      isFixed: true,
      type: 'religious'
    },
    {
      date: new Date(year, 10, 1),  // November 1
      namePL: 'Wszystkich Świętych',
      nameEN: "All Saints' Day",
      isFixed: true,
      type: 'religious'
    },
    {
      date: new Date(year, 10, 11),  // November 11
      namePL: 'Narodowe Święto Niepodległości',
      nameEN: 'Independence Day',
      isFixed: true,
      type: 'national'
    },
    {
      date: new Date(year, 11, 25),  // December 25
      namePL: 'Boże Narodzenie (pierwszy dzień)',
      nameEN: 'Christmas Day',
      isFixed: true,
      type: 'religious'
    },
    {
      date: new Date(year, 11, 26),  // December 26
      namePL: 'Boże Narodzenie (drugi dzień)',
      nameEN: 'Boxing Day',
      isFixed: true,
      type: 'religious'
    }
  ];

  // Variable holidays (3 total - Easter-dependent)
  const variableHolidays: Holiday[] = [
    {
      date: new Date(easter),  // Easter Sunday (Easter + 0 days)
      namePL: 'Wielkanoc (Niedziela Wielkanocna)',
      nameEN: 'Easter Sunday',
      isFixed: false,
      type: 'religious'
    },
    {
      date: addDays(easter, 1),  // Easter Monday (Easter + 1 day)
      namePL: 'Poniedziałek Wielkanocny',
      nameEN: 'Easter Monday',
      isFixed: false,
      type: 'religious'
    },
    {
      date: addDays(easter, 49),  // Pentecost (Easter + 49 days)
      namePL: 'Zielone Świątki (Niedziela Zielonych Świątek)',
      nameEN: 'Pentecost',
      isFixed: false,
      type: 'religious'
    }
  ];

  // Combine and sort by date
  const allHolidays = [...fixedHolidays, ...variableHolidays];
  allHolidays.sort((a, b) => a.date.getTime() - b.date.getTime());

  return allHolidays;
}

/**
 * Check if a given date is a Polish public holiday
 *
 * @param date - Date to check (time component is ignored)
 * @returns True if date is a Polish public holiday, false otherwise
 */
export function isPolishHoliday(date: Date): boolean {
  // Input validation
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error('Invalid date provided');
  }

  // Get all holidays for the year
  const year = date.getFullYear();
  const holidays = getPolishHolidays(year);

  // Compare dates (ignoring time component)
  const targetDate = normalizeDate(date);
  return holidays.some(holiday => isSameDate(holiday.date, targetDate));
}

/**
 * Get day type for tariff calculation
 *
 * Returns 'weekday', 'weekend', or 'holiday' classification.
 * Used in G13 tariff calculations where holidays = weekends (off-peak rates).
 *
 * @param date - Date to classify
 * @returns Day type
 */
export function getDayType(date: Date): DayType {
  // Check if holiday first (highest priority)
  if (isPolishHoliday(date)) {
    return 'holiday';
  }

  // Check if weekend (Sunday = 0, Saturday = 6)
  const dayOfWeek = date.getDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return 'weekend';
  }

  return 'weekday';
}

/**
 * Get G13 tariff rate zone based on date and time
 *
 * G13 tariff has three rate zones:
 * - Peak: 6:00-10:00, 15:00-21:00 (weekdays only)
 * - Off-peak: 10:00-15:00, 21:00-22:00 (weekdays), all day weekends/holidays
 * - Night: 22:00-6:00 (all days)
 *
 * @param dateTime - Full date-time to evaluate
 * @returns Rate zone
 */
export function getG13RateZone(dateTime: Date): G13RateZone {
  const dayType = getDayType(dateTime);
  const hour = dateTime.getHours();

  // Weekends and holidays: off-peak all day (except night hours)
  if (dayType === 'weekend' || dayType === 'holiday') {
    if (hour >= 22 || hour < 6) {
      return 'night';
    }
    return 'offpeak';
  }

  // Weekdays: time-based rates
  // Peak: 6:00-10:00, 15:00-21:00
  if ((hour >= 6 && hour < 10) || (hour >= 15 && hour < 21)) {
    return 'peak';
  }

  // Night: 22:00-6:00
  if (hour >= 22 || hour < 6) {
    return 'night';
  }

  // Off-peak: 10:00-15:00, 21:00-22:00
  return 'offpeak';
}

// ============================================================================
// HELPER FUNCTIONS (Internal)
// ============================================================================

/**
 * Add days to a date (returns new Date object)
 * @private
 */
function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Normalize date to midnight (remove time component)
 * @private
 */
function normalizeDate(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Compare two dates (ignoring time component)
 * @private
 */
function isSameDate(date1: Date, date2: Date): boolean {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
}

// ============================================================================
// VALIDATION DATA
// ============================================================================

/**
 * Official Easter dates for validation (2025-2030)
 */
export const OFFICIAL_EASTER_DATES: Record<number, Date> = {
  2025: new Date(2025, 3, 20),  // April 20
  2026: new Date(2026, 3, 5),   // April 5
  2027: new Date(2027, 2, 28),  // March 28
  2028: new Date(2028, 3, 16),  // April 16
  2029: new Date(2029, 3, 1),   // April 1
  2030: new Date(2030, 3, 21)   // April 21
};

/**
 * Validation Result Type
 */
export interface ValidationResult {
  calculated: string;
  official: string;
  match: boolean;
}

/**
 * Validate Easter calculation against official dates
 */
export function validateEasterCalculation(): Record<number, ValidationResult> {
  const results: Record<number, ValidationResult> = {};

  for (const [yearStr, officialDate] of Object.entries(OFFICIAL_EASTER_DATES)) {
    const year = parseInt(yearStr);
    const calculatedDate = calculateEasterSunday(year);
    const match = isSameDate(calculatedDate, officialDate);

    results[year] = {
      calculated: calculatedDate.toISOString().split('T')[0],
      official: officialDate.toISOString().split('T')[0],
      match
    };
  }

  return results;
}

export default {
  calculateEasterSunday,
  getPolishHolidays,
  isPolishHoliday,
  getDayType,
  getG13RateZone,
  validateEasterCalculation,
  OFFICIAL_EASTER_DATES
};
